/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import { TextField, Button, Modal, Backdrop, Fade, Alert, Box, CircularProgress, Stack, Tooltip } from "@mui/material";
import { DetailedUser, Effect, ModalBase } from "../../../utils/Interfaces";
import { createUser, refreshUsers } from "./userManagementFunctions";
import { selectExistingUsers } from "./userManagementSlice";
import { delayedAction, validateEmail, validateUsername } from "../../../utils/Functions";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export interface CreateUserModalProps extends ModalBase {
    onRefreshRequested: Effect;
}

const CreateUserModal = (props: CreateUserModalProps) => {
    const { open, handleClose } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [loading, setLoading] = useState<boolean>(false);

    const [username, setUsername] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [email, setEmail] = useState<string>("");

    const existingUsers: DetailedUser[] = useAppSelector(selectExistingUsers);

    useEffect(() => {
        // Only perform reload when opening the create-user modal.
        if (open) {
            refreshUsers(dispatch, setLoading);
        }
    }, [open]);

    function usernameTaken() {
        return (
            existingUsers.filter(
                (user: DetailedUser): boolean => user.username.trim().toLowerCase() === username.trim().toLowerCase()
            ).length > 0
        );
    }

    function emailTaken() {
        return (
            existingUsers.filter((user: DetailedUser): boolean => user.email.trim().toLowerCase() === email.trim().toLowerCase())
                .length > 0
        );
    }

    function getFormErrors(): string {
        if (username.trim() === "") {
            return "Username is blank.";
        }
        if (usernameTaken()) {
            return "Username in use.";
        }
        if (!validateUsername(username)) {
            return "Username is not valid.";
        }
        if (firstName.trim() === "") {
            return "First Name is blank.";
        }
        if (lastName.trim() === "") {
            return "Last Name is blank.";
        }
        if (email.trim() === "") {
            return "Email is blank.";
        }
        if (emailTaken()) {
            return "Email in use.";
        }
        if (!validateEmail(email.trim())) {
            return "Email is not valid.";
        }

        return "";
    }

    function getFormWarnings(): string {
        if (getFormErrors() === "") {
            if (!email.endsWith("@techwiseis.com")) {
                return "Email is outside of the Techwise Domain.";
            }
        }
        return "";
    }

    function close() {
        handleClose();
        void delayedAction(500, () => {
            setUsername("");
            setFirstName("");
            setLastName("");
            setEmail("");
            setLoading(false);
        });
    }

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: (theme) => theme.shadows[5],
                        padding: (theme) => theme.spacing(2, 4, 3),
                        zIndex: 1000,
                    }}
                >
                    {getFormErrors() && <Alert severity="error">{getFormErrors()}</Alert>}

                    {getFormWarnings() && <Alert severity="warning">{getFormWarnings()}</Alert>}

                    {!getFormErrors() && !getFormWarnings() && <Alert severity="success">No errors detected</Alert>}

                    <TextField
                        onChange={(event) => {
                            setUsername(event.target.value);
                        }}
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Username"
                        name="username"
                        value={username}
                        error={usernameTaken() || !validateUsername(username)}
                    />

                    <TextField
                        onChange={(event) => {
                            setFirstName(event.target.value);
                        }}
                        margin="normal"
                        required
                        fullWidth
                        id="first_name"
                        label="First Name"
                        name="first_name"
                        value={firstName}
                    />

                    <TextField
                        onChange={(event) => {
                            setLastName(event.target.value);
                        }}
                        margin="normal"
                        required
                        fullWidth
                        id="last_name"
                        label="Last Name"
                        name="last_name"
                        value={lastName}
                    />

                    <TextField
                        onChange={(event) => {
                            setEmail(event.target.value);
                        }}
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        error={emailTaken() || !validateEmail(email)}
                        value={email}
                    />

                    <Stack direction="row" justifyContent="center">
                        {loading && <CircularProgress size={20} sx={{ mt: 2.5, mb: 1 }} />}
                        {!loading && (
                            <>
                                <Tooltip title="Discard changes">
                                    <Button variant="contained" onClick={close} sx={{ mr: 1, mt: 2 }}>
                                        Cancel
                                    </Button>
                                </Tooltip>

                                <Tooltip title="Create new user">
                                    <span>
                                        <Button
                                            variant="contained"
                                            disabled={getFormErrors() !== ""}
                                            onClick={() => {
                                                setLoading(true);
                                                createUser(
                                                    dispatch,
                                                    {
                                                        username: username.trim(),
                                                        first_name: firstName.trim(),
                                                        last_name: lastName.trim(),
                                                        email: email.trim(),
                                                    },
                                                    () => {},
                                                    () => refreshUsers(dispatch, close)
                                                );
                                            }}
                                            sx={{ ml: 1, mt: 2 }}
                                            color="success"
                                        >
                                            Create
                                        </Button>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    );
};

export default CreateUserModal;
